import React, { ReactElement, useEffect } from 'react';
import moment from 'moment';
import { Form, Grid, Header } from 'semantic-ui-react';
import { useTranslation, withTranslation } from 'react-i18next';

import IGeneralInformationContainer from './GeneralInformation/IGeneralInformationContainer';
import IEnvironmentalInformationContainer from './EnvironmentalInformation/IEnvironmentalInformationContainer';
import ICheckedInformationContainer from './CheckedInformation/ICheckedInformationContainer';
import ChemicalProductDetailsContainer from '../../ChemicalProduct/ChemicalProductDetails/ChemicalProductDetailsContainer';
import ICheckedCseInformationContainer from './CheckedInformation/ICheckedCseInformationContainer';
import IChemicalRiskInformationContainer from './ChemicalRiskInformation/IChemicalRiskInformationContainer';
import { getInventoryTitle } from '../../../utils';
import { useTitleDispatch } from '../../TitleContextProvider';
import { defaultDateFormat } from '../../../Dates';
import InventoryDetailsCreateModeSteps from './InventoryDetailsCreateModeSteps';
import { Mode } from '../../../constants';
import i18n from 'i18next';
import { AccordionWrapper } from 'react-lib/dist/components-es';

type Props = {
  values: any;
  buttons: any;
  mode: Mode;
  currentStep: number;
  validateAndSetCurrentStep: any;
  formikProps: any;
  isHistory: boolean;
  setFieldValue: (arg1: string, arg2: string | null) => void;
  initialData: any;
  errors: any;
  entityTypeProp: string;
  setOverridePersonState: (arg: boolean) => void;
  validateOnChange: boolean;
  setValues: (arg: any) => void;
  disabled: boolean;
  setValidateOnChange: (arg: boolean) => void;
};

const InventoryDetailsForm = ({
  values,
  buttons,
  mode,
  currentStep,
  validateAndSetCurrentStep,
  formikProps,
  isHistory,
  setFieldValue,
  initialData,
  errors,
  entityTypeProp,
  setOverridePersonState,
  validateOnChange,
  setValues,
  disabled,
  setValidateOnChange,
}: Props) => {
  const { t } = useTranslation();
  const title = getInventoryTitle(values, t);
  const dispatch = useTitleDispatch();
  const isStep1 = currentStep === 1;

  useEffect(() => {
    let historyDate = values.histCreateDte
      ? moment(values.histCreateDte).format(defaultDateFormat)
      : null;
    let isDeleted = values.delDte ? moment(values.delDte).isBefore(moment.now()) : false;

    let pageTitle = t('page.inventory.create.new');
    if (title) {
      if (!isHistory) {
        pageTitle = t(`inventory.details.page.title.${mode}`, { title: title });
      } else {
        pageTitle = t(
          isDeleted
            ? 'inventory.details.page.deleted.title'
            : 'inventory.details.page.history.title',
          {
            title: title,
            histDate: historyDate,
            histUser: values.histCreateUser.name,
          }
        );
      }
    }

    dispatch({
      pageTitle: pageTitle,
    });
  }, [mode, i18n.language]);

  const getCenteredGridComponent = (component: ReactElement, className = '') => {
    return (
      <Grid stackable columns={1} className={className}>
        {isStep1 && <Grid.Column tablet={2} computer={4} only="tablet computer" />}
        <Grid.Column
          tablet={isStep1 ? 12 : 16}
          computer={isStep1 ? 8 : 16}
          className="detailsGridCol"
        >
          {component}
        </Grid.Column>
      </Grid>
    );
  };

  return (
    <Form id="inventoryDetailsForm" className="smallScreenReduceLeftRightPadding">
      {mode === Mode.CREATE && (
        <Grid stackable columns={1}>
          <Grid.Column className="detailsGridCol">
            <InventoryDetailsCreateModeSteps
              currentStep={currentStep}
              setCurrentStep={validateAndSetCurrentStep}
            />
          </Grid.Column>
        </Grid>
      )}
      {getCenteredGridComponent(buttons)}
      <Grid stackable columns={2}>
        {!isStep1 && (
          <Grid.Column className="detailsGridCol">
            <Header as="h2">{title || <br />}</Header>
            <AccordionWrapper
              accordionTitle={t('inventory.details.page.inventory.details.container.title')}
              className="containerReducedWhiteSpace"
              values={values}
              render={(props: any) => (
                <>
                  <IGeneralInformationContainer
                    values={values}
                    errors={errors}
                    setFieldValue={setFieldValue}
                    isHistory={isHistory}
                    initialData={initialData}
                    mode={mode}
                    entityTypeProp={entityTypeProp}
                    setOverridePersonState={setOverridePersonState}
                    validateOnChange={validateOnChange}
                    disabled={disabled}
                  />
                  <IEnvironmentalInformationContainer
                    values={values}
                    initialData={initialData}
                    setFieldValue={setFieldValue}
                    errors={errors}
                    setValues={setValues}
                    disabled={disabled}
                  />
                  <IChemicalRiskInformationContainer
                    values={values}
                    mode={mode}
                    setFieldValue={setFieldValue}
                    initialData={initialData}
                    errors={errors}
                    setOverridePersonState={setOverridePersonState}
                    validateOnChange={validateOnChange}
                    disabled={disabled}
                    setValidateOnChange={setValidateOnChange}
                  />
                  {mode !== Mode.CREATE && mode !== Mode.CLONE && (
                    <ICheckedInformationContainer
                      checkedMode="last"
                      initialData={initialData}
                      values={values}
                      setFieldValue={setFieldValue}
                      setValidateOnChange={setValidateOnChange}
                      formikProps={formikProps}
                      errors={errors}
                    />
                  )}
                  <ICheckedCseInformationContainer
                    initialData={initialData}
                    values={values}
                    setFieldValue={setFieldValue}
                    setValidateOnChange={setValidateOnChange}
                    disabled={disabled}
                    errors={errors}
                  />
                </>
              )}
            />
          </Grid.Column>
        )}
        {isStep1 && <Grid.Column tablet={2} computer={4} only="computer tablet" />}
        <Grid.Column className="detailsGridCol" tablet={isStep1 ? 12 : 8} computer={8}>
          <ChemicalProductDetailsContainer
            values={values}
            setFieldValue={setFieldValue}
            initialData={initialData}
            errors={errors}
            setValues={setValues}
            setValidateOnChange={setValidateOnChange}
            disabled={disabled}
            formikProps={formikProps}
            mode={mode}
            setOverridePersonState={setOverridePersonState}
            validateOnChange={validateOnChange}
            currentStep={currentStep}
          />
        </Grid.Column>
      </Grid>
      {getCenteredGridComponent(buttons)}
    </Form>
  );
};

export default React.memo(withTranslation()(InventoryDetailsForm));
